import { LitElement, css, html, nothing } from 'lit';
import { parseMapLink, type ReleaseManifest } from '@ka/contracts';
import { readProgress, resetProgress, shared, spiderSvg, webCorner } from '@ka/ui';
import { hasNewerRelease, loadRelease, type LoadResult } from './data.ts';
import * as pages from './pages.ts';
import { setEmitter, startRouter, type Route } from './router.ts';
import './map-view.ts';

const NAV = [
  ['/episodes', 'Episodes'],
  ['/map', 'Map'],
  ['/characters', 'Characters'],
  ['/lore', 'Lore'],
  ['/about', 'About'],
] as const;

export class KaApp extends LitElement {
  static override properties = {
    data: { state: true },
    route: { state: true },
    newer: { state: true },
    tick: { state: true },
  };
  declare data: LoadResult | null;
  declare route: Route | null;
  declare newer: boolean;
  declare tick: number;

  static override styles = [
    shared,
    css`
      .skip {
        position: absolute;
        left: -999px;
      }
      .skip:focus {
        left: 1rem;
        top: 1rem;
        z-index: 10;
      }
      header {
        background: var(--ka-stone-800);
        border-bottom: 3px solid #000;
        box-shadow: var(--ka-bevel);
      }
      .wrap {
        max-width: var(--ka-max);
        margin: 0 auto;
        padding: 0 1rem;
      }
      header .wrap {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        min-height: 4rem;
      }
      .brand {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        font: 600 1.2rem var(--ka-font-display);
        color: var(--ka-parchment);
        text-decoration: none;
      }
      .brand svg {
        color: var(--ka-red);
      }
      nav ul {
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;
        list-style: none;
        margin: 0;
        padding: 0;
      }
      nav a {
        display: inline-flex;
        align-items: center;
        min-height: 44px;
        padding: 0 0.75rem;
        color: var(--ka-parchment);
        text-decoration: none;
        font-family: var(--ka-font-display);
      }
      nav a[aria-current='page'] {
        color: var(--ka-gold);
        box-shadow: inset 0 -3px 0 var(--ka-red);
      }
      main {
        padding-block: 2rem 4rem;
        display: grid;
        gap: 2rem;
      }
      footer {
        border-top: 3px solid #000;
        background: var(--ka-stone-800);
        padding-block: 1.5rem;
        font-size: 0.9rem;
      }
      .banner {
        padding: 0.6rem 1rem;
        text-align: center;
        font: 600 1rem var(--ka-font-display);
        border-bottom: 3px solid #000;
      }
      .banner.draft {
        background: var(--ka-red);
        color: #fff;
      }
      .banner.fixture {
        background: var(--ka-gold);
        color: #111;
      }
      .banner.newer {
        background: var(--ka-foliage-dim);
      }
      .hero {
        padding: 2rem 0;
      }
      .kicker {
        font-family: var(--ka-font-display);
        color: var(--ka-foliage);
        margin: 0 0 0.25rem;
      }
      .lede {
        font-size: 1.2rem;
        max-width: 60ch;
      }
      .ctas {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
      }
      .plain {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      .books {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        gap: 1.25rem;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 15rem), 1fr));
      }
      .book a {
        display: grid;
        gap: 0.25rem;
        padding: 0.75rem 0.75rem 1rem 1.25rem;
        background: linear-gradient(90deg, #3b2418 0 10px, #5c3a26 10px);
        border: 2px solid #000;
        box-shadow:
          var(--ka-bevel),
          4px 4px 0 #000;
        color: var(--ka-parchment);
        text-decoration: none;
        transition: transform 0.15s;
      }
      .book a:hover,
      .book a:focus-visible {
        transform: translateY(-3px);
      }
      .book img,
      .book-cover {
        width: 100%;
        aspect-ratio: 16 / 9;
        object-fit: cover;
        border: 2px solid #000;
        background: var(--ka-stone-900);
        display: grid;
        place-items: center;
        color: var(--ka-red);
      }
      .book-num {
        font: 600 0.85rem var(--ka-font-display);
        color: var(--ka-gold);
      }
      .book-title {
        font: 600 1.15rem var(--ka-font-display);
      }
      .char {
        border-left: 6px solid var(--ka-foliage);
      }
      .accent-red.char,
      .accent-red .tag {
        border-color: var(--ka-red);
      }
      .accent-purple.char,
      .accent-purple .tag {
        border-color: var(--ka-purple);
      }
      .two-col {
        display: grid;
        gap: 1.5rem;
        grid-template-columns: minmax(0, 2fr) minmax(16rem, 1fr);
        align-items: start;
      }
      .two-col.char-page {
        grid-template-columns: minmax(14rem, 1fr) minmax(0, 2fr);
      }
      @media (max-width: 800px) {
        .two-col,
        .two-col.char-page {
          grid-template-columns: 1fr;
        }
      }
      .placeholder {
        text-align: center;
        padding: 1rem;
        color: var(--ka-parchment-dim);
      }
      .chapters button.link,
      button.link {
        all: unset;
        cursor: pointer;
        color: var(--ka-gold);
        text-decoration: underline;
        min-height: 44px;
        min-width: 44px;
      }
      button.link:focus-visible {
        outline: 3px solid var(--ka-focus);
      }
      .scroll {
        background: linear-gradient(#efe3c4, #e2d3ab);
        color: #2a2016;
        max-width: 70ch;
      }
      .scroll a {
        color: #7a1f19;
      }
      .scroll .tag {
        color: #2a2016;
      }
      .hidden-spider {
        justify-self: end;
        opacity: 0.6;
      }
      .web {
        position: absolute;
        top: 0;
        right: 0;
        color: var(--ka-parchment);
      }
      .state {
        text-align: center;
        padding: 4rem 1rem;
      }
    `,
  ];

  constructor() {
    super();
    this.data = null;
    this.route = null;
    this.newer = false;
    this.tick = 0;
  }

  override connectedCallback() {
    super.connectedCallback();
    void loadRelease().then((d) => (this.data = d));
    setEmitter(startRouter((r) => this.onRoute(r)));
    window.addEventListener('ka-secret-progress', () => this.tick++);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.data?.kind === 'release') {
        void hasNewerRelease(this.data.siteRevision).then((n) => (this.newer = n));
      }
    });
  }

  private onRoute(r: Route) {
    const changedPage = this.route?.name !== r.name || this.route?.params.slug !== r.params.slug;
    this.route = r;
    if (changedPage) {
      window.scrollTo(0, 0);
      // Move focus to the new page heading for screen reader and keyboard users.
      void this.updateComplete.then(() =>
        this.renderRoot.querySelector<HTMLElement>('main h1')?.focus(),
      );
    }
  }

  private secretPanel(m: ReleaseManifest) {
    const s = m.secrets[0];
    if (!s) return nothing;
    const p = readProgress(s.progressVersion);
    const found = s.puzzle.symbolIds.filter((id) => p.found.includes(id));
    const done = found.length === s.puzzle.symbolIds.length;
    const reward = m.lore.find((l) => l.id === s.rewardLoreId);
    return html`<section class="panel" aria-live="polite">
      ${webCorner}
      <h2>${spiderSvg(20)} ${s.title}</h2>
      <p>Spiders found: ${found.length} of ${s.puzzle.symbolIds.length}</p>
      ${
        done && reward
          ? html`<article class="panel scroll">
              <h3>${reward.title}</h3>
              <ka-markdown .source=${reward.body}></ka-markdown>
            </article>`
          : html`<details>
              <summary>Need a hint?</summary>
              <p>${s.puzzle.hint}</p>
            </details>`
      }
      <p>
        <button class="btn" @click=${() => resetProgress(s.progressVersion)}>
          Reset spider hunt
        </button>
      </p>
      <p class="muted">Progress is saved only in this browser.</p>
    </section>`;
  }

  private page(): pages.Page | null {
    const d = this.data;
    const r = this.route;
    if (!d || !r) return null;
    if (d.kind === 'error')
      return { title: 'Error', body: html`<p class="panel state" role="alert">${d.message}</p>` };
    const m = d.kind === 'release' ? d.manifest : null;
    if (r.name === 'home') return pages.home(m);
    if (r.name === 'about') return pages.about(m);
    if (!m) return pages.home(null);
    switch (r.name) {
      case 'episodes':
        return pages.episodes(m);
      case 'episode':
        return pages.episode(m, r);
      case 'map':
        return {
          title: 'Map',
          body: html`<ka-map-view .manifest=${m} .link=${parseMapLink(r.query)}></ka-map-view>`,
        };
      case 'characters':
        return pages.characters(m);
      case 'character':
        return pages.character(m, r);
      case 'lore':
        return pages.lore(m, new Set(m.secrets.map((s) => s.rewardLoreId)), this.secretPanel(m));
      case 'loreEntry':
        return pages.loreEntry(m, r);
      default:
        return pages.notFound();
    }
  }

  override updated() {
    const p = this.page();
    if (p)
      document.title =
        p.title === 'King Arachnid Chronicles' ? p.title : `${p.title} · King Arachnid Chronicles`;
    this.renderRoot.querySelector('main h1')?.setAttribute('tabindex', '-1');
  }

  override render() {
    const m = this.data?.kind === 'release' ? this.data.manifest : null;
    const path = location.pathname;
    const p = this.page();
    return html`
      <a class="skip btn" href="#main">Skip to content</a>
      ${m?.draft ? html`<div class="banner draft" role="status">DRAFT PREVIEW — not published</div>` : nothing}
      ${m?.fixture ? html`<div class="banner fixture" role="status">FIXTURE DATA — sample content for development, not real episodes</div>` : nothing}
      ${
        this.newer
          ? html`<div class="banner newer" role="status">
              A new adventure is available!
              <button class="btn" @click=${() => location.reload()}>Refresh</button>
            </div>`
          : nothing
      }
      <header>
        <div class="wrap">
          <a class="brand" href="/">${spiderSvg(28)} King Arachnid</a>
          <nav aria-label="Main">
            <ul>
              ${NAV.map(([href, label]) => html`<li><a href=${href} aria-current=${path.startsWith(href) ? 'page' : 'false'}>${label}</a></li>`)}
            </ul>
          </nav>
        </div>
      </header>
      <main id="main" class="wrap">
        ${p ? p.body : html`<p class="state" role="status">Loading…</p>`}
      </main>
      <footer>
        <div class="wrap muted">
          Unofficial fan project. Not an official Minecraft product. Not approved by or associated
          with Mojang or Microsoft.
        </div>
      </footer>
    `;
  }
}
customElements.define('ka-app', KaApp);
